// EventUtils.js - Utility functions for calendar events

// Function to check if a date is in the past
export const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day for proper comparison

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    return compareDate < today;
};

// Helper function to generate recurring dates
export const generateRecurringDates = (eventData) => {
    if (!eventData.repeat || !eventData.repeatType) return [eventData];

    const events = [];
    let currentStart = new Date(eventData.start);
    let currentEnd = new Date(eventData.end);
    const until = eventData.repeatUntil ? new Date(eventData.repeatUntil) : null;

    // Get the duration between start and end for consistency
    const duration = currentEnd - currentStart;

    // Limit to reasonable number of occurrences to prevent infinite loops
    const maxOccurrences = 100;
    let count = 0;

    while ((!until || currentStart <= until) && count < maxOccurrences) {
        // Skip if not matching the repeat pattern
        const dayOfWeek = currentStart.getDay();
        if (
            (eventData.repeatType === 'weekdays' && (dayOfWeek === 0 || dayOfWeek === 6)) ||
            (eventData.repeatType === 'weekends' && (dayOfWeek > 0 && dayOfWeek < 6))
        ) {
            // Move to next day
            currentStart = new Date(currentStart);
            currentStart.setDate(currentStart.getDate() + 1);
            currentEnd = new Date(currentStart.getTime() + duration);
            continue;
        }

        // Clone the event data for this occurrence
        const occurrence = {
            ...eventData,
            start: new Date(currentStart),
            end: new Date(currentEnd),
            parentEventId: count === 0 ? null : eventData.id,
            repeat: true,
            repeatType: eventData.repeatType,
            repeatUntil: eventData.repeatUntil
        };

        // Only set ID for existing events or generated instances
        if (eventData.id) {
            occurrence.id = count === 0 ? eventData.id : `generated-${eventData.id}-${count}`;
        }

        events.push(occurrence);
        count++;

        // Calculate next occurrence based on repeat type
        switch(eventData.repeatType) {
            case 'daily':
                currentStart.setDate(currentStart.getDate() + 1);
                break;
            case 'weekdays':
            case 'weekends':
                currentStart.setDate(currentStart.getDate() + 1);
                break;
            case 'weekly':
                currentStart.setDate(currentStart.getDate() + 7);
                break;
            case 'biweekly':
                currentStart.setDate(currentStart.getDate() + 14);
                break;
            case 'monthly':
                currentStart.setMonth(currentStart.getMonth() + 1);
                break;
            default:
                currentStart.setDate(currentStart.getDate() + 1);
        }

        currentEnd = new Date(currentStart.getTime() + duration);
    }

    // If we have a repeatUntil date and the last event is before it, add one more event
    if (until && events.length > 0) {
        const lastEvent = events[events.length - 1];
        const lastEventDate = new Date(lastEvent.start);
        lastEventDate.setHours(0, 0, 0, 0);
        const untilDate = new Date(until);
        untilDate.setHours(0, 0, 0, 0);

        if (lastEventDate < untilDate) {
            const nextStart = new Date(lastEvent.start);
            const nextEnd = new Date(lastEvent.end);

            switch(eventData.repeatType) {
                case 'daily':
                    nextStart.setDate(nextStart.getDate() + 1);
                    break;
                case 'weekdays':
                case 'weekends':
                    nextStart.setDate(nextStart.getDate() + 1);
                    break;
                case 'weekly':
                    nextStart.setDate(nextStart.getDate() + 7);
                    break;
                case 'biweekly':
                    nextStart.setDate(nextStart.getDate() + 14);
                    break;
                case 'monthly':
                    nextStart.setMonth(nextStart.getMonth() + 1);
                    break;
                default:
                    nextStart.setDate(nextStart.getDate() + 1);
            }

            nextEnd.setTime(nextStart.getTime() + duration);

            if (nextStart <= until) {
                const finalOccurrence = {
                    ...eventData,
                    start: nextStart,
                    end: nextEnd,
                    parentEventId: eventData.id,
                    repeat: true,
                    repeatType: eventData.repeatType,
                    repeatUntil: eventData.repeatUntil
                };

                if (eventData.id) {
                    finalOccurrence.id = `generated-${eventData.id}-${count}`;
                }

                events.push(finalOccurrence);
            }
        }
    }

    return events;
};

// Function to get todo color based on priority
export const getTodoColor = (priority) => {
    switch(priority) {
        case 'high': return '#F44336';
        case 'medium': return '#FF9800';
        case 'low': return '#4CAF50';
        default: return '#9E9E9E';
    }
};